import { logger } from '@/lib/logger';
import React, { useState, useEffect, createContext, useContext, useRef } from 'react';
import { db } from '@/lib/firebase';
import { collection, getDocs, query, limit } from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';

import { playTone, NOTIFICATION_TONES } from '@/lib/notificationSounds';

const NotificationContext = createContext({});

const REAL_STORAGE_KEY = 'oblique_real_notifications';
const MATRIX_STORAGE_KEY = 'oblique_notification_preferences_matrix';

export const NOTIFICATION_CHANNELS = [
    { id: 'popup', label: 'Pop-up', icon: 'picture_in_picture', desc: 'In-app corner popups' },
    { id: 'browser', label: 'Browser', icon: 'desktop_windows', desc: 'Desktop OS push notifications' },
    { id: 'bell', label: 'Bell', icon: 'notifications', desc: 'Bell icon badge & drawer' },
    { id: 'email', label: 'Email', icon: 'mail', desc: 'Digest or instant email dispatch' },
    { id: 'mobile', label: 'Mobile', icon: 'phone_iphone', desc: 'Push notifications on iOS & Android' },
    { id: 'slack', label: 'Slack', icon: 'chat', desc: 'Channel pings or direct Slack bots' }
];

export const DEFAULT_NOTIFICATION_TOPICS = [
    {
        id: 'academy',
        name: 'Academy',
        description: 'Training courses, certifications, and learning milestones',
        subtopics: [
            { id: 'acad-courses', name: 'Course Enrollment', desc: 'Assigned a required training course or SOP', channels: { popup: false, browser: false, bell: true, email: true, mobile: null, slack: false } },
            { id: 'acad-assessments', name: 'Assessment Results', desc: 'An audit assessment or compliance test is graded', channels: { popup: false, browser: false, bell: true, email: true, mobile: null, slack: false } },
            { id: 'acad-cert', name: 'Certification Issued', desc: 'A certificate or mastery badge is awarded', channels: { popup: true, browser: false, bell: true, email: true, mobile: null, slack: false } }
        ]
    },
    {
        id: 'account',
        name: 'Account',
        description: 'Team membership, permissions, and security alerts',
        subtopics: [
            { id: 'acc-invites', name: 'User Invitations', desc: 'A team member joins your agency or workspace', channels: { popup: false, browser: false, bell: true, email: true, mobile: null, slack: false } },
            { id: 'acc-roles', name: 'Role & Permissions', desc: 'Your role, permissions, or seat access changes', channels: { popup: false, browser: true, bell: true, email: true, mobile: null, slack: false } },
            { id: 'acc-security', name: 'Security & Login', desc: 'High-priority login alert or password reset request', channels: { popup: true, browser: true, bell: true, email: true, mobile: null, slack: true } }
        ]
    },
    {
        id: 'account_settings',
        name: 'Account Settings',
        description: 'Billing, integrations, and webhook system diagnostics',
        subtopics: [
            { id: 'set-billing', name: 'Subscription & Invoices', desc: 'Plan renewals, invoice generated, payment notice', channels: { popup: false, browser: false, bell: true, email: true, mobile: null, slack: false } },
            { id: 'set-webhooks', name: 'Apps Script & Webhooks', desc: 'Automated data sync, webhook failures, API triggers', channels: { popup: true, browser: true, bell: true, email: false, mobile: null, slack: true } },
            { id: 'set-quota', name: 'Storage & Quota Warnings', desc: 'Workspace approaching database or file limit', channels: { popup: true, browser: true, bell: true, email: true, mobile: null, slack: true } }
        ]
    },
    {
        id: 'qms',
        name: 'Quality Audits & QMS',
        description: 'QA audits, call monitoring, lead reviews, and escalations',
        subtopics: [
            { id: 'qms-task', name: 'Audit Task Assigned', desc: 'You are assigned a call, lead, or deal audit task', channels: { popup: true, browser: true, bell: true, email: true, mobile: true, slack: true } },
            { id: 'qms-escalation', name: 'Critical Escalations & RCA', desc: 'An urgent escalation or RCA has been filed', channels: { popup: true, browser: true, bell: true, email: true, mobile: true, slack: true } },
            { id: 'qms-scorecard', name: 'Audit Verdict Finalized', desc: 'A QA score, scorecard dispute, or resolution updated', channels: { popup: false, browser: false, bell: true, email: true, mobile: false, slack: false } }
        ]
    },
    {
        id: 'sales',
        name: 'Sales & Deals',
        description: 'CRM deals, 2nd voice closer assignments, and pipeline changes',
        subtopics: [
            { id: 'sales-won', name: 'Deal Closed Won', desc: 'A client deal closes or renews successfully', channels: { popup: true, browser: true, bell: true, email: true, mobile: true, slack: true } },
            { id: 'sales-2ndvoice', name: '2nd Voice / Closer Assigned', desc: 'You or a teammate is added as 2nd voice closer', channels: { popup: true, browser: true, bell: true, email: false, mobile: true, slack: false } },
            { id: 'sales-stage', name: 'Deal Stage Changed', desc: 'Pipeline milestone changes (e.g. Contract Sent)', channels: { popup: false, browser: false, bell: true, email: false, mobile: false, slack: false } }
        ]
    },
    {
        id: 'mentions',
        name: 'Mentions & Comments',
        description: 'Direct mentions in deal notes, activity feeds, and tickets',
        subtopics: [
            { id: 'men-deal', name: 'Mentioned in Deal Note', desc: '@mention in a deal note, activity feed, or CRM note', channels: { popup: true, browser: true, bell: true, email: true, mobile: true, slack: true } },
            { id: 'men-qms', name: 'Comment on Audit Scorecard', desc: 'QA auditor or manager leaves feedback on your audit', channels: { popup: true, browser: true, bell: true, email: true, mobile: true, slack: false } },
            { id: 'men-ping', name: 'Direct Team Ping', desc: 'Direct team mention in internal communication', channels: { popup: true, browser: true, bell: true, email: true, mobile: true, slack: true } }
        ]
    }
];

// Strictly real-world baseline notifications requested by the user, with exact HubSpot mention replicas
const REAL_WORLD_BASELINE_NOTIFICATIONS = [
    {
        id: 'hubspot-mention-silky',
        app: 'mentions',
        appLabel: 'Mention',
        type: 'mention',
        author: 'Muskan Gupta',
        title: 'You were mentioned on "silkyhairskincare.net"',
        message: 'Muskan Gupta mentioned you in a deal note on silkyhairskincare.net: "Please review the SEO audit report."',
        entityDomain: 'silkyhairskincare.net',
        time: new Date(Date.now() - 1000 * 60 * 18),
        read: false,
        trashed: false,
        link: '/quality/sales-audits'
    },
    {
        id: 'hubspot-mention-silicon',
        app: 'mentions',
        appLabel: 'Mention',
        type: 'mention',
        author: 'Kabir Coley',
        title: 'You were mentioned on "siliconpublishing.com"',
        message: 'Kabir Coley mentioned you: "Max Dunn deal 2nd voice closer assigned to team."',
        entityDomain: 'siliconpublishing.com',
        time: new Date(Date.now() - 1000 * 60 * 65),
        read: false,
        trashed: false,
        link: '/quality/sales-audits'
    },
    {
        id: 'real-admin-pw-1',
        app: 'admin',
        appLabel: 'Superadmin',
        type: 'alert',
        title: 'Password Reset Request: Diksha Khatri',
        message: 'Workspace: The Scale Agency · Reset verification requested for diksha@thescaleagency.com',
        time: new Date(Date.now() - 1000 * 60 * 120),
        read: false,
        trashed: false,
        link: '/admin?tab=users'
    },
    {
        id: 'real-automation-fail-1',
        app: 'automation',
        appLabel: 'Automation',
        type: 'alert',
        title: 'Apps Script Failed: Lead Sync Webhook',
        message: 'Google Apps Script trigger exception: HTTP 429 Too Many Requests in daily HubSpot sync',
        time: new Date(Date.now() - 1000 * 60 * 180),
        read: false,
        trashed: false,
        link: '/automations'
    },
    {
        id: 'real-risk-1',
        app: 'risk',
        appLabel: 'Risk Alert',
        type: 'alert',
        title: 'Compliance Risk: SLA Breached',
        message: 'Lead audit for Cool Robot exceeded 4h SLA threshold (Score: 62% - At Risk)',
        time: new Date(Date.now() - 1000 * 60 * 240),
        read: true,
        trashed: false,
        link: '/quality/leads'
    },
    {
        id: 'real-data-export-1',
        app: 'data',
        appLabel: 'Data & Export',
        type: 'success',
        title: 'Large File Export Complete: 14,800 Deals',
        message: 'CSV export "Deals_Audit_Q4_TheScaleAgency.csv" generated successfully (12.4 MB)',
        time: new Date(Date.now() - 1000 * 60 * 300),
        read: true,
        trashed: false,
        link: '/quality/sales-audits'
    },
    {
        id: 'real-transfer-1',
        app: 'collab',
        appLabel: 'Transfers',
        type: 'transfer',
        title: 'Report Transferred: QMS Weekly Audit',
        message: 'Diksha Khatri sent verified audit package to Kabir Coley (Team Naved)',
        time: new Date(Date.now() - 1000 * 60 * 380),
        read: true,
        trashed: false,
        link: '/quality/audits?type=calls'
    }
];

const loadSavedNotifications = () => {
    if (typeof window === 'undefined') return REAL_WORLD_BASELINE_NOTIFICATIONS;
    try {
        const saved = localStorage.getItem(REAL_STORAGE_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed)) {
                // Strictly filter out any old mock artifacts or auto-polled DB collections
                const clean = parsed.filter(n => 
                    n && n.id &&
                    !n.id.startsWith('order-') &&
                    !n.id.startsWith('crm-deal-') &&
                    !n.id.startsWith('qms-call-') &&
                    !n.id.startsWith('orbit-') &&
                    !n.id.startsWith('threat-') &&
                    !n.id.startsWith('init-qms') &&
                    !n.id.startsWith('init-crm')
                );
                if (clean.length > 0) {
                    // Ensure the two essential HubSpot mention notifications are present
                    const hasSilky = clean.some(n => n.id === 'hubspot-mention-silky');
                    let list = clean.map(n => ({
                        ...n,
                        time: n.time ? new Date(n.time) : new Date(),
                        trashed: Boolean(n.trashed)
                    }));
                    if (!hasSilky) {
                        list = [REAL_WORLD_BASELINE_NOTIFICATIONS[0], REAL_WORLD_BASELINE_NOTIFICATIONS[1], ...list];
                    }
                    return list;
                }
            }
        }
    } catch (e) {
        logger.error('Error loading real notifications:', e);
    }
    return REAL_WORLD_BASELINE_NOTIFICATIONS;
};

const loadSavedMatrix = () => {
    if (typeof window === 'undefined') return DEFAULT_NOTIFICATION_TOPICS;
    try {
        const saved = localStorage.getItem(MATRIX_STORAGE_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed) && parsed.length > 0) {
                return parsed;
            }
        }
    } catch (e) {
        logger.error('Error loading notification matrix:', e);
    }
    return DEFAULT_NOTIFICATION_TOPICS;
};

/**
 * Notification Provider - Unified Real-Time Notification Engine for Oblique
 * Strictly handles real-world workspace notifications with zero automated mock spam.
 */
export function NotificationProvider({ children }) {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState(loadSavedNotifications);
    const [notificationMatrix, setNotificationMatrix] = useState(loadSavedMatrix);
    const [storageInfo, setStorageInfo] = useState({
        totalDocs: 0,
        estimatedBytes: 0,
        estimatedGB: 0,
        percentUsed: 0,
        status: 'healthy'
    });
    const [unreadCount, setUnreadCount] = useState(() => {
        const list = loadSavedNotifications();
        return list.filter(n => !n.read && !n.trashed).length;
    });
    const [isRinging, setIsRinging] = useState(false);
    
    // Sound preference & Tone selection
    const [soundEnabled, setSoundEnabled] = useState(() => {
        const saved = localStorage.getItem('oblique_notifications_sound');
        return saved !== null ? JSON.parse(saved) : true;
    });

    const [selectedTone, setSelectedTone] = useState(() => {
        return localStorage.getItem('oblique_notification_tone') || 'apple-glass';
    });

    // Native Desktop OS Notification Preference (Windows & macOS)
    const [desktopEnabled, setDesktopEnabled] = useState(() => {
        const saved = localStorage.getItem('oblique_notifications_desktop');
        return saved !== null ? JSON.parse(saved) : false;
    });

    const [desktopPermission, setDesktopPermission] = useState(() => {
        return typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'default';
    });

    const soundEnabledRef = useRef(soundEnabled);
    useEffect(() => {
        soundEnabledRef.current = soundEnabled;
    }, [soundEnabled]);

    const selectedToneRef = useRef(selectedTone);
    useEffect(() => {
        selectedToneRef.current = selectedTone;
    }, [selectedTone]);

    const desktopEnabledRef = useRef(desktopEnabled);
    useEffect(() => {
        desktopEnabledRef.current = desktopEnabled;
    }, [desktopEnabled]);

    // Keep unreadCount synchronized whenever notifications list updates
    useEffect(() => {
        const unread = notifications.filter(n => !n.read && !n.trashed).length;
        setUnreadCount(unread);
    }, [notifications]);

    // Bell ring animation trigger
    const triggerBellRing = () => {
        setIsRinging(true);
        const isLongTone = ['marimba', 'bell-ring'].includes(selectedToneRef.current);
        setTimeout(() => setIsRinging(false), isLongTone ? 1600 : 1100);
    };

    // Play synthesized chime or ringtone via Web Audio API
    const playNotificationSound = (customTone) => {
        if (!soundEnabledRef.current && !customTone) return;
        const toneToPlay = customTone || selectedToneRef.current;
        playTone(toneToPlay);
    };

    // Preview specific tone (for selection preview)
    const previewTone = (toneId) => {
        playTone(toneId);
    };

    // Free Native OS Notification (Windows Action Center & macOS Notification Center)
    const sendDesktopNotification = (title, message, link) => {
        if (typeof window === 'undefined' || !('Notification' in window)) return;
        if (Notification.permission === 'granted' && desktopEnabledRef.current) {
            try {
                const notif = new Notification(title, {
                    body: message,
                    icon: '/favicon.svg',
                    badge: '/favicon.svg',
                    silent: !soundEnabledRef.current
                });
                notif.onclick = () => {
                    window.focus();
                    if (link) window.location.href = link;
                };
            } catch (e) {
                logger.warn('Desktop notification error:', e);
            }
        }
    };

    // Request native OS notification permission
    const requestDesktopPermission = async () => {
        if (typeof window === 'undefined' || !('Notification' in window)) {
            return 'unsupported';
        }
        try {
            const permission = await Notification.requestPermission();
            setDesktopPermission(permission);
            if (permission === 'granted') {
                setDesktopEnabled(true);
                localStorage.setItem('oblique_notifications_desktop', 'true');
                sendDesktopNotification(
                    'Desktop Notifications Active', 
                    'You will receive real-time notifications in Windows & Mac notification centers.',
                    '#'
                );
            }
            return permission;
        } catch (e) {
            return 'denied';
        }
    };

    const toggleDesktopNotifications = async () => {
        if (typeof window === 'undefined' || !('Notification' in window)) return;
        if (Notification.permission !== 'granted') {
            await requestDesktopPermission();
        } else {
            const next = !desktopEnabled;
            setDesktopEnabled(next);
            localStorage.setItem('oblique_notifications_desktop', JSON.stringify(next));
        }
    };

    const STORAGE_LIMIT_GB = 1;
    const STORAGE_LIMIT_BYTES = STORAGE_LIMIT_GB * 1024 * 1024 * 1024;
    const AVG_DOC_SIZE = 5 * 1024;

    // Background storage usage health check only (no notification polling)
    useEffect(() => {
        if (!user) return;
        checkStorageUsage();
        const storageInterval = setInterval(checkStorageUsage, 60 * 60 * 1000);
        return () => {
            clearInterval(storageInterval);
        };
    }, [user]);

    // Real-Time Event Listener for In-App triggers & Webhooks
    useEffect(() => {
        const handleCustomNotification = (event) => {
            const notifData = event.detail;
            if (notifData) {
                addNotification(notifData);
            }
        };

        window.addEventListener('oblique-new-notification', handleCustomNotification);
        return () => {
            window.removeEventListener('oblique-new-notification', handleCustomNotification);
        };
    }, []);

    // Global helper accessible on window for Google Apps Scripts / Automations / Console
    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.triggerObliqueNotification = (data) => {
                window.dispatchEvent(new CustomEvent('oblique-new-notification', { detail: data }));
            };
        }
    }, []);

    const checkStorageUsage = async () => {
        try {
            const collections_list = ['cases', 'leads', 'deals', 'call_audits', 'activities', 'app_users'];
            let totalDocs = 0;

            for (const collName of collections_list) {
                try {
                    const snapshot = await getDocs(query(collection(db, collName), limit(10000)));
                    totalDocs += snapshot.size;
                } catch { /* Collection might not exist */ }
            }

            const estimatedBytes = totalDocs * AVG_DOC_SIZE;
            const estimatedGB = estimatedBytes / (1024 * 1024 * 1024);
            const percentUsed = (estimatedBytes / STORAGE_LIMIT_BYTES) * 100;

            let status = 'healthy';
            if (percentUsed >= 90) status = 'critical';
            else if (percentUsed >= 70) status = 'warning';

            setStorageInfo({
                totalDocs,
                estimatedBytes,
                estimatedGB: estimatedGB.toFixed(3),
                percentUsed: percentUsed.toFixed(1),
                status
            });
        } catch (error) {
            logger.error('Error checking storage:', error);
        }
    };

    /**
     * Add real notification (triggered by actual export, transfer, password reset, risk, or Apps Script event)
     */
    const addNotification = ({ app = 'system', appLabel = 'System', title, message, link, type = 'system', entityDomain, author }) => {
        const newNotif = {
            id: `realtime-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
            app,
            appLabel,
            type,
            title,
            message,
            entityDomain,
            author,
            time: new Date(),
            read: false,
            trashed: false,
            link: link || '#'
        };

        setNotifications(prev => {
            const updated = [newNotif, ...prev];
            try {
                localStorage.setItem(REAL_STORAGE_KEY, JSON.stringify(updated));
            } catch {}
            return updated;
        });
        
        // Trigger sound, visual bell ring animation, and OS notification for real incoming event
        playNotificationSound();
        triggerBellRing();
        sendDesktopNotification(title, message, link);
    };

    const markAllRead = () => {
        setNotifications(prev => {
            const updated = prev.map(n => ({ ...n, read: true }));
            try {
                localStorage.setItem(REAL_STORAGE_KEY, JSON.stringify(updated));
            } catch {}
            return updated;
        });
        setUnreadCount(0);
    };

    const markAsRead = (ids) => {
        setNotifications(prev => {
            const updated = prev.map(n => ids.includes(n.id) ? { ...n, read: true } : n);
            try {
                localStorage.setItem(REAL_STORAGE_KEY, JSON.stringify(updated));
            } catch {}
            return updated;
        });
    };

    const markAsUnread = (ids) => {
        setNotifications(prev => {
            const updated = prev.map(n => ids.includes(n.id) ? { ...n, read: false } : n);
            try {
                localStorage.setItem(REAL_STORAGE_KEY, JSON.stringify(updated));
            } catch {}
            return updated;
        });
    };

    // Move to Trash (Soft Delete)
    const moveToTrash = (ids) => {
        setNotifications(prev => {
            const updated = prev.map(n => ids.includes(n.id) ? { ...n, trashed: true } : n);
            try {
                localStorage.setItem(REAL_STORAGE_KEY, JSON.stringify(updated));
            } catch {}
            return updated;
        });
    };

    // Restore from Trash
    const restoreFromTrash = (ids) => {
        setNotifications(prev => {
            const updated = prev.map(n => ids.includes(n.id) ? { ...n, trashed: false } : n);
            try {
                localStorage.setItem(REAL_STORAGE_KEY, JSON.stringify(updated));
            } catch {}
            return updated;
        });
    };

    // Empty entire trash permanently
    const emptyTrash = () => {
        setNotifications(prev => {
            const updated = prev.filter(n => !n.trashed);
            try {
                localStorage.setItem(REAL_STORAGE_KEY, JSON.stringify(updated));
            } catch {}
            return updated;
        });
    };

    // Permanently Delete Specific IDs
    const deletePermanently = (ids) => {
        setNotifications(prev => {
            const updated = prev.filter(n => !ids.includes(n.id));
            try {
                localStorage.setItem(REAL_STORAGE_KEY, JSON.stringify(updated));
            } catch {}
            return updated;
        });
    };

    const dismissNotification = (id) => {
        moveToTrash([id]);
    };

    const clearAllNotifications = () => {
        setNotifications(prev => {
            const updated = prev.map(n => ({ ...n, trashed: true }));
            try {
                localStorage.setItem(REAL_STORAGE_KEY, JSON.stringify(updated));
            } catch {}
            return updated;
        });
        setUnreadCount(0);
    };

    // ── NOTIFICATION PREFERENCES MATRIX ACTIONS ─────────────────────
    const saveMatrixState = (matrix) => {
        setNotificationMatrix(matrix);
        try {
            localStorage.setItem(MATRIX_STORAGE_KEY, JSON.stringify(matrix));
        } catch {}
    };

    const updateTopicChannel = (categoryId, topicId, channel, value) => {
        const updated = notificationMatrix.map(cat => {
            if (cat.id !== categoryId) return cat;
            return {
                ...cat,
                subtopics: cat.subtopics.map(sub => {
                    if (sub.id !== topicId) return sub;
                    return {
                        ...sub,
                        channels: {
                            ...sub.channels,
                            [channel]: value
                        }
                    };
                })
            };
        });
        saveMatrixState(updated);
    };

    const toggleCategoryChannel = (categoryId, channel) => {
        const cat = notificationMatrix.find(c => c.id === categoryId);
        if (!cat) return;
        const applicable = cat.subtopics.filter(s => s.channels[channel] !== null);
        if (applicable.length === 0) return;
        const allEnabled = applicable.every(s => Boolean(s.channels[channel]));
        const nextVal = !allEnabled;

        const updated = notificationMatrix.map(c => {
            if (c.id !== categoryId) return c;
            return {
                ...c,
                subtopics: c.subtopics.map(s => {
                    if (s.channels[channel] === null) return s;
                    return {
                        ...s,
                        channels: {
                            ...s.channels,
                            [channel]: nextVal
                        }
                    };
                })
            };
        });
        saveMatrixState(updated);
    };

    const toggleMasterChannel = (channel) => {
        const allApplicable = [];
        notificationMatrix.forEach(cat => {
            cat.subtopics.forEach(sub => {
                if (sub.channels[channel] !== null) {
                    allApplicable.push(sub.channels[channel]);
                }
            });
        });
        const allEnabled = allApplicable.every(val => Boolean(val));
        const nextVal = !allEnabled;

        const updated = notificationMatrix.map(cat => ({
            ...cat,
            subtopics: cat.subtopics.map(sub => {
                if (sub.channels[channel] === null) return sub;
                return {
                    ...sub,
                    channels: {
                        ...sub.channels,
                        [channel]: nextVal
                    }
                };
            })
        }));
        saveMatrixState(updated);
    };

    const turnOffAllTopics = () => {
        const updated = notificationMatrix.map(cat => ({
            ...cat,
            subtopics: cat.subtopics.map(sub => ({
                ...sub,
                channels: Object.keys(sub.channels).reduce((acc, ch) => {
                    acc[ch] = sub.channels[ch] === null ? null : false;
                    return acc;
                }, {})
            }))
        }));
        saveMatrixState(updated);
    };

    const resetChannelDefaults = () => {
        saveMatrixState(DEFAULT_NOTIFICATION_TOPICS);
    };

    return (
        <NotificationContext.Provider value={{
            notifications,
            storageInfo,
            unreadCount,
            isRinging,
            triggerBellRing,
            markAllRead,
            markAsRead,
            markAsUnread,
            addNotification,
            dismissNotification,
            moveToTrash,
            restoreFromTrash,
            emptyTrash,
            deletePermanently,
            clearAllNotifications,
            deleteNotifications: moveToTrash,
            refreshStorage: checkStorageUsage,
            refreshOrders: () => {},
            soundEnabled,
            setSoundEnabled: (val) => {
                setSoundEnabled(val);
                localStorage.setItem('oblique_notifications_sound', JSON.stringify(val));
                if (val) playNotificationSound();
            },
            selectedTone,
            setSelectedTone: (toneId) => {
                setSelectedTone(toneId);
                localStorage.setItem('oblique_notification_tone', toneId);
                playTone(toneId);
            },
            availableTones: NOTIFICATION_TONES,
            previewTone,
            desktopEnabled,
            setDesktopEnabled: (val) => {
                setDesktopEnabled(val);
                localStorage.setItem('oblique_notifications_desktop', JSON.stringify(val));
            },
            desktopPermission,
            requestDesktopPermission,
            toggleDesktopNotifications,
            playNotificationSound,
            // Matrix State & Helpers
            notificationMatrix,
            notificationChannels: NOTIFICATION_CHANNELS,
            updateTopicChannel,
            toggleCategoryChannel,
            toggleMasterChannel,
            turnOffAllTopics,
            resetChannelDefaults
        }}>
            {children}
        </NotificationContext.Provider>
    );
}

export const useNotifications = () => useContext(NotificationContext);
export const useNotification = useNotifications;
